/**
 * Pure math for the Home chart「目標達成に必要」line.
 * Output shape matches a future API field (`required[]`) so LineChart stays
 * render-only when the series later moves server-side.
 *
 * Forecast-shaped blend (Option A):
 *   F[lastActual] = lastActualValue; F[i] = forecast[i] when present.
 *   If F[deadline] usable: req = last + (F-last)*(target-last)/(F_dead-last).
 *   If deadline off-chart: same with last on-chart F, scaled by steps_anchor/steps_deadline.
 *   Else linear lastActual→target by month progress.
 *   Distinct planMonths waypoints win (echo of final target ignored). The path ends at the
 *   deadline month — a 3-month goal draws 3 months of 必要, whatever the chart window.
 */

/**
 * How much of a month's remaining gap to 目標 a 予測 bump may close. Below 1 so the
 * path stays clear of 目標 until the deadline month, which is the only one allowed to
 * sit on it. Raise it for a tighter hug of 予測's shape, lower it for more daylight.
 */
import { resolvePlanPeriod } from './planPeriod';

const DEVIATION_HEADROOM = 0.9;

function parseMonthKey(monthKey) {
  if (!monthKey || typeof monthKey !== 'string') return null;
  const [y, m] = monthKey.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return null;
  return { year: y, month: m };
}

/** Whole months from `fromKey` to `toKey` (can be negative). */
export function monthDiff(fromKey, toKey) {
  const a = parseMonthKey(fromKey);
  const b = parseMonthKey(toKey);
  if (!a || !b) return null;
  return (b.year - a.year) * 12 + (b.month - a.month);
}

export function monthKeyOffset(monthKey, deltaMonths) {
  const p = parseMonthKey(monthKey);
  if (!p || !Number.isFinite(deltaMonths)) return null;
  const d = new Date(p.year, p.month - 1 + deltaMonths, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** True when deadline month is still after last actual (usable for a path). */
function isFutureDeadline(lastActualMonthKey, goalMonthKey) {
  if (!goalMonthKey || !/^\d{4}-\d{2}$/.test(goalMonthKey)) return false;
  if (!lastActualMonthKey) return true;
  const d = monthDiff(lastActualMonthKey, goalMonthKey);
  return d != null && d > 0;
}

/**
 * Resolve deadline month key from goals.
 * Precedence: plan period (planStartMonth/goalDate + planLength) → goalDate → last planMonths.monthKey → lastActual + planLength.
 * Stale deadlines (≤ last actual) are skipped so the chart does not go flat at target.
 * No deadline → null. The period is the goal record's job to carry, not this
 * function's to invent; a goal saved without one is a save-side bug.
 */
export function resolveGoalMonthKey(goals, lastActualMonthKey) {
  if (!goals) return { goalMonthKey: null, source: 'legacy' };

  // One plan period for every screen (utils/planPeriod): the chart's deadline is the same
  // month the Goals roadmap ends on — start month + planLength, not the stored goalDate.
  // Only when the record carries a start anchor; planLength alone keeps the old
  // lastActual + planLength rule below (no "today" leaks into the chart window).
  if (goals.planStartMonth || goals.goalDate) {
    const period = resolvePlanPeriod(goals);
    if (period && isFutureDeadline(lastActualMonthKey, period.endMonth)) {
      return { goalMonthKey: period.endMonth, source: 'planPeriod' };
    }
  }

  if (goals.goalDate) {
    const m = String(goals.goalDate).slice(0, 7);
    if (/^\d{4}-\d{2}$/.test(m) && isFutureDeadline(lastActualMonthKey, m)) {
      return { goalMonthKey: m, source: 'goalDate' };
    }
  }

  const plan = Array.isArray(goals.planMonths) ? goals.planMonths : [];
  const keys = plan
    .map((p) => p?.monthKey)
    .filter((k) => /^\d{4}-\d{2}$/.test(k || ''))
    .sort();
  if (keys.length > 0) {
    const last = keys[keys.length - 1];
    if (isFutureDeadline(lastActualMonthKey, last)) {
      return { goalMonthKey: last, source: 'planMonths' };
    }
  }

  const rawLen = Number(goals.planLength);
  if (
    lastActualMonthKey &&
    Number.isFinite(rawLen) &&
    rawLen >= 1 &&
    rawLen <= 24
  ) {
    const mk = monthKeyOffset(lastActualMonthKey, rawLen);
    if (mk) return { goalMonthKey: mk, source: 'planLength' };
  }

  return { goalMonthKey: null, source: 'legacy' };
}

/**
 * Share of the total climb that should already be done by month `stepI`.
 *
 * Equal monthly steps are only right when every month is equally capable. They are
 * not: 12月 forecasts ¥785k against 11月's ¥650k, so asking for the same increment in
 * both is what made the line a featureless diagonal — and this is the *fallback*, so
 * any failure in the blend above silently produced exactly that straight ramp with no
 * way to tell from the chart. Weighting each step by its own 予測 level keeps the path
 * monotonic, still lands exactly on 目標, and shows where the push actually has to be.
 *
 * `weights` are the future 予測 values (index-aligned); missing/degenerate data falls
 * back to equal steps, which reproduces the old straight ramp.
 */
function progressAtStep(stepI, stepsToDeadline, weights) {
  if (stepI >= stepsToDeadline) return 1;
  if (!Array.isArray(weights) || weights.length < stepsToDeadline) {
    return stepI / stepsToDeadline;
  }
  let done = 0;
  let total = 0;
  for (let k = 0; k < stepsToDeadline; k += 1) {
    const w = Number(weights[k]);
    const safe = Number.isFinite(w) && w > 0 ? w : 0;
    total += safe;
    if (k < stepI) done += safe;
  }
  if (!(total > 0)) return stepI / stepsToDeadline;
  return done / total;
}

function shapedAtProgress(start, tgt, lastActualMonth, mk, stepsToDeadline, weights) {
  if (stepsToDeadline <= 0) return tgt;
  const stepI = monthDiff(lastActualMonth, mk);
  if (stepI == null) return null;
  return start + (tgt - start) * progressAtStep(stepI, stepsToDeadline, weights);
}

/**
 * @param {object} args
 * @param {string[]} args.monthKeys
 * @param {number} args.lastActualIdx
 * @param {number|null} args.lastActualValue
 * @param {number|null} args.target
 * @param {string|null} args.goalMonthKey
 * @param {Record<string, number>} [args.planTargets]
 * @param {string} [args.source]
 * @param {(number|null)[]|null} [args.forecast] aligned to monthKeys
 * @returns {{ series: (number|null)[]|null, meta: object }}
 */
/**
 * Build a complete 予測 shape from lastActualIdx..throughIdx.
 * F[lastActualIdx] is forced to `start` (latest 実績 on the graph).
 * Null gaps are linear-interpolated between known points (hold at ends).
 */
function densifyForecastShape(forecast, lastActualIdx, start, throughIdx) {
  const n = Array.isArray(forecast) ? forecast.length : 0;
  const F = Array.from({ length: n }, () => null);
  if (n === 0 || lastActualIdx < 0 || throughIdx < lastActualIdx) return F;

  for (let i = 0; i < n; i += 1) {
    if (forecast[i] != null && !Number.isNaN(Number(forecast[i]))) {
      F[i] = Number(forecast[i]);
    }
  }
  F[lastActualIdx] = Number(start);

  let i = lastActualIdx;
  while (i < throughIdx) {
    let j = i + 1;
    while (j <= throughIdx && F[j] == null) j += 1;
    if (j > throughIdx) {
      for (let k = i + 1; k <= throughIdx; k += 1) F[k] = F[i];
      break;
    }
    const span = j - i;
    for (let k = i + 1; k < j; k += 1) {
      const t = (k - i) / span;
      F[k] = F[i] + (F[j] - F[i]) * t;
    }
    i = j;
  }
  return F;
}

export function buildRequiredSeries({
  monthKeys,
  lastActualIdx,
  lastActualValue,
  target,
  goalMonthKey,
  planTargets = {},
  source = 'deadline',
  forecast = null,
}) {
  const legacyMeta = {
    source: 'legacy',
    goalMonthKey: null,
    deadlineIdx: -1,
    stepsToDeadline: null,
    shape: 'none',
  };

  if (
    !Array.isArray(monthKeys) ||
    monthKeys.length === 0 ||
    lastActualIdx == null ||
    lastActualIdx < 0 ||
    lastActualIdx >= monthKeys.length ||
    lastActualValue == null ||
    Number.isNaN(Number(lastActualValue)) ||
    target == null ||
    Number.isNaN(Number(target))
  ) {
    return { series: null, meta: legacyMeta };
  }

  if (!goalMonthKey || !/^\d{4}-\d{2}$/.test(goalMonthKey)) {
    return { series: null, meta: legacyMeta };
  }

  const lastActualMonth = monthKeys[lastActualIdx];
  const stepsToDeadline = monthDiff(lastActualMonth, goalMonthKey);
  if (stepsToDeadline == null) {
    return { series: null, meta: legacyMeta };
  }

  const tgt = Number(target);
  const start = Number(lastActualValue);
  const deadlineIdx = monthKeys.indexOf(goalMonthKey);

  // Densify 予測 on the chart: force F[lastActual] = latest 実績 (same start as the graph),
  // lerp gaps so the mokuhyo path can keep the full 予測 pattern.
  const throughIdx =
    deadlineIdx >= lastActualIdx ? deadlineIdx : Math.max(lastActualIdx, monthKeys.length - 1);
  const F = densifyForecastShape(forecast, lastActualIdx, start, throughIdx);

  // Path to mokuhyo = ramp start→target, plus 予測's own seasonal wiggle
  // (F minus F's own straight trend across the future window). Scaling the raw
  // deviations from `start` instead would blow the mid path far above the target,
  // especially when 予測 begins on a different level than the latest 実績.
  const firstFutureIdx = lastActualIdx + 1;
  const fEnd = F[throughIdx] != null ? Number(F[throughIdx]) : null;
  const fFirst =
    firstFutureIdx <= throughIdx && F[firstFutureIdx] != null ? Number(F[firstFutureIdx]) : null;
  const trendSpan = throughIdx - firstFutureIdx;
  const forecastAtDeadline = deadlineIdx >= lastActualIdx ? fEnd : null;
  // Densify holds the last known value forward, so require at least one real future
  // 予測 point before calling the path forecast-shaped.
  const hasFutureForecast =
    Array.isArray(forecast) &&
    forecast.some(
      (v, i) =>
        i > lastActualIdx && i <= throughIdx && v != null && !Number.isNaN(Number(v))
    );
  const canBlend =
    stepsToDeadline > 0 && fEnd != null && fFirst != null && hasFutureForecast;

  const shape = canBlend ? 'forecast_blend' : 'linear';

  const goalDirection = tgt > start ? 1 : tgt < start ? -1 : 0;
  const aheadOfTarget =
    forecastAtDeadline != null &&
    goalDirection !== 0 &&
    (goalDirection === 1 ? forecastAtDeadline >= tgt : forecastAtDeadline <= tgt);

  // 予測 level for each future step (step 1 = first month after 実績), used to weight
  // the climb so the fallback path is shaped instead of a bare diagonal.
  const stepWeights = [];
  for (let s = 1; s <= stepsToDeadline; s += 1) {
    const idx = lastActualIdx + s;
    const v = idx < F.length ? F[idx] : null;
    stepWeights.push(v != null && Number.isFinite(Number(v)) ? Number(v) : 0);
  }

  // How much curvature the blend's deviation term actually carries. Near zero means
  // F is already a straight line (real data, or densify having interpolated over
  // gaps), so the blend would hand back the plain ramp — that is when the weighted
  // climb has to take over, or the chart shows a featureless diagonal.
  let maxDeviation = 0;
  if (canBlend && trendSpan > 0) {
    for (let i = firstFutureIdx; i <= throughIdx; i += 1) {
      if (F[i] == null) continue;
      const trend = fFirst + ((fEnd - fFirst) * (i - firstFutureIdx)) / trendSpan;
      maxDeviation = Math.max(maxDeviation, Math.abs(Number(F[i]) - trend));
    }
  }
  // Needs at least three future points: with two, the trend line passes through both
  // by construction and the deviation is 0 for arithmetic reasons, not because 予測 is
  // featureless. Those short horizons keep the plain ramp.
  const climb = Math.abs(tgt - start);
  const deviationIsFlat =
    canBlend && trendSpan >= 3 && climb > 0 && maxDeviation < climb * 0.005;

  const rampAtStep = (stepI) =>
    start +
    (tgt - start) *
      (deviationIsFlat
        ? progressAtStep(stepI, stepsToDeadline, stepWeights)
        : stepI / stepsToDeadline);

  // Only the deadline month may sit on 目標. Deviations are added on top of the ramp,
  // so a month whose 予測 towers over its own trend used to clear 目標 and get clipped
  // there — the path then reached the goal a month or two early and flat-lined on it.
  // Shrink every deviation by one shared factor, sized off the tightest month, so the
  // worst offender lands just under 目標 and the 予測 shape (dips included) survives.
  const deviationScale = (() => {
    if (!canBlend || trendSpan <= 0 || goalDirection === 0) return 1;
    let scale = 1;
    for (let i = firstFutureIdx; i <= throughIdx; i += 1) {
      if (F[i] == null) continue;
      const stepI = i - lastActualIdx;
      if (stepI <= 0 || stepI >= stepsToDeadline) continue; // deadline month is pinned
      const trend = fFirst + ((fEnd - fFirst) * (i - firstFutureIdx)) / trendSpan;
      const dev = (Number(F[i]) - trend) * goalDirection;
      if (dev <= 0) continue; // deviations away from 目標 can never reach it
      const room = (tgt - rampAtStep(stepI)) * goalDirection * DEVIATION_HEADROOM;
      if (room <= 0) return 0;
      if (dev > room) scale = Math.min(scale, room / dev);
    }
    return scale;
  })();

  const series = monthKeys.map((mk, i) => {
    if (i < lastActualIdx) return null;

    const planVal = planTargets?.[mk];
    const atOrPastDeadline = deadlineIdx >= 0 && i >= deadlineIdx;
    if (
      !atOrPastDeadline &&
      planVal != null &&
      !Number.isNaN(Number(planVal)) &&
      Number(planVal) !== tgt
    ) {
      return Number(planVal);
    }

    if (stepsToDeadline <= 0) return tgt;
    if (i > deadlineIdx && deadlineIdx >= 0) return null; // the goal period is over: no 必要 to draw
    // Same starting point as latest 実績 on the graph — always.
    if (i === lastActualIdx) return start;
    // Deadline month = mokuhyo target for the period (hard pin).
    if (deadlineIdx >= 0 && i === deadlineIdx) return tgt;

    if (canBlend && F[i] != null) {
      const stepI = monthDiff(lastActualMonth, mk);
      if (stepI != null && stepI > 0) {
        // Equal steps normally; weighted steps when the deviation term is degenerate.
        // densifyForecastShape linearly interpolates across gaps, so a 予測 with holes
        // becomes a straight F — then `Fi - forecastTrend` is 0 at every month and the
        // path collapsed to a bare diagonal while still reporting shape='forecast_blend'.
        const ramp = rampAtStep(stepI);
        const forecastTrend =
          trendSpan > 0
            ? fFirst + ((fEnd - fFirst) * (i - firstFutureIdx)) / trendSpan
            : Number(F[i]);
        const Fi = Number(F[i]);
        const blended = ramp + (Fi - forecastTrend) * deviationScale;
        // Mid path stays inside the reachable band: never past mokuhyo (the target is
        // the period end), and never worse than both the latest 実績 and 予測 — a large
        // 予測 spike must not push the path to absurd lows.
        if (goalDirection === 1) return Math.min(Math.max(blended, Math.min(start, Fi)), tgt);
        if (goalDirection === -1) return Math.max(Math.min(blended, Math.max(start, Fi)), tgt);
        return blended;
      }
    }

    return shapedAtProgress(start, tgt, lastActualMonth, mk, stepsToDeadline, stepWeights);
  });

  return {
    series,
    meta: {
      source: source || 'deadline',
      goalMonthKey,
      deadlineIdx,
      stepsToDeadline,
      shape,
      aheadOfTarget,
    },
  };
}

/**
 * One consistent plan for every chart, solved from the 売上 required path and
 * the 注力 pins — drivers first.
 *
 * THE RULE (2026-09-10, replaces the 2026-08-18 proportional split):
 *
 *   1. 売上 is pinned to the KGI target (done by the caller, not here). It is the
 *      spine; nothing below may contradict it.
 *   2. A 注力 KPI is a PIN — a floor, not an equality. Its 実績→目標 path is held
 *      only while the solution would otherwise fall short of it; a pin the plan
 *      clears anyway is released (`meta.relaxedPins`), so 指名客数 20 at 指名率 50%
 *      cannot cap 客数 at 40 when the goal needs 90 customers.
 *   3. Four drivers move 売上:  新規客数, リピート客数, 指名率, フリー客単価
 *
 *        売上 = (新規 + リピ) × フリー単価 × (1 + 指名率 × (k − 1))
 *
 *      where k = 指名客単価 ÷ フリー客単価 read from this stylist's own recent
 *      history (nominated customers pay more — 指名料, longer menus). 指名率 is
 *      the driver; the mix is only its transmission to 客単価. No 指名 sales data
 *      → k = 1 and 指名率 has no lever, so it is left out of the free set.
 *   4. Drivers the pins do not fix are FREE. The lift the goal demands is shared
 *      by all of them through one scalar effort t: multiplier = exp(t × w_d),
 *      w_d = how much that driver historically moves for this stylist (CV).
 *      t is solved so 売上 lands exactly on the required path. A pinned quantity
 *      always beats a derivation from another pin (リピ pin > 来店周期 → リピ).
 *   5. Everything else is identity:
 *        店販 = pin, else closed-month share × 売上;   技術 = 売上 − 店販
 *        客数 = 新規 + リピ;   客単価 = 売上 ÷ 客数;   技術単価 = 技術 ÷ 客数
 *        技単新規/技単リピ = forecast, scaled so 新規×技単新 + リピ×技単リピ = 技術
 *        指名客数 = 客数 × 指名率
 *        来店周期 ← リピ (visits ∝ 1 ÷ interval, soft; never longer than 予測)
 *        新規再来率: forecast unless pinned; a pin adds 新規(m−3) × Δ再来率 to リピ.
 *   6. A personal range is a FLAG, never a cap. If the goal needs 客単価 above the
 *      stylist's best month, the line shows it and `meta.feasibility` says so.
 *      A goal the pins alone make impossible (e.g. 指名客数 20 at 指名率 50% fixes
 *      客数 at 40) is reported the same way — the number is drawn, not hidden.
 *
 * @param {object} args
 * @param {string[]} args.monthKeys
 * @param {number} args.lastActualIdx
 * @param {(number|null)[]} args.revenueRequired  売上必要 (pinned by the caller)
 * @param {Record<string,(number|null)[]>} [args.pins]  focusKey → required path
 * @param {Record<string,object>} [args.historyByMonth]  API history rows by month
 * @param {Record<string,object>} [args.forecastByMonth] API forecast rows by month
 * @returns {{ series: Record<string,(number|null)[]|null>, meta: object }}
 */
export function solveDriverPlan({
  monthKeys,
  lastActualIdx,
  revenueRequired,
  pins = {},
  historyByMonth = {},
  forecastByMonth = {},
}) {
  const keys = [
    'techSales', 'retailSales', 'customerUnitPrice', 'techUnitPrice',
    'newCustomerCount', 'repeatCustomerCount', 'customerCount',
    'newTechnicalUnitPrice', 'repeatTechnicalUnitPrice',
    'nominationRate', 'nominationCustomers', 'visitFrequency', 'newReturn',
  ];
  const empty = Object.fromEntries(keys.map((k) => [k, null]));
  const none = { series: empty, meta: { source: 'none', published: [], freeDrivers: [], feasibility: [] } };

  if (
    !Array.isArray(monthKeys) || monthKeys.length === 0 ||
    !Array.isArray(revenueRequired) || revenueRequired.length !== monthKeys.length ||
    lastActualIdx == null || lastActualIdx < 0 || lastActualIdx >= monthKeys.length
  ) {
    return none;
  }

  const anchorRow = readDriverRow(historyByMonth[monthKeys[lastActualIdx]]);
  if (!anchorRow || anchorRow.sales == null) return none;

  const histRows = Object.entries(historyByMonth)
    .filter(([mk]) => mk <= monthKeys[lastActualIdx])
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, r]) => readDriverRow(r))
    .filter((r) => r && r.sales != null && r.count);
  const premium = nominationPremium(histRows);
  const K = premium - 1;
  const weights = driverWeights(histRows);
  const band = personalBand(histRows);
  const histRetailShare = (() => {
    const rows = histRows.slice(-3).filter((r) => r.retail != null && r.sales);
    if (!rows.length) return null;
    return rows.reduce((acc, r) => acc + r.retail, 0) / rows.reduce((acc, r) => acc + r.sales, 0);
  })();
  let relaxedPins = []; // pins released in the last solved month (= the deadline month)
  const relaxedByMonth = {};

  const P = (focusKey, i) => {
    const s = pins[focusKey];
    return Array.isArray(s) && s[i] != null && !Number.isNaN(Number(s[i])) ? Number(s[i]) : null;
  };

  const out = Object.fromEntries(keys.map((k) => [k, monthKeys.map(() => null)]));
  const feasibility = [];
  let freeDrivers = [];
  let retailOverran = false;
  let pinsFixEverything = false;

  monthKeys.forEach((mk, i) => {
    if (i < lastActualIdx) return;
    const R = num(revenueRequired[i]);
    if (R == null) return;

    if (i === lastActualIdx) {
      // Anchor month sits on 実績 — a fact, not a plan.
      const a = anchorRow;
      out.retailSales[i] = a.retail;
      out.techSales[i] = a.sales - (a.retail ?? 0);
      out.customerCount[i] = a.count;
      out.newCustomerCount[i] = a.newC;
      out.repeatCustomerCount[i] = a.repC;
      out.customerUnitPrice[i] = a.count ? a.sales / a.count : null;
      out.techUnitPrice[i] = a.count ? (a.sales - (a.retail ?? 0)) / a.count : null;
      out.newTechnicalUnitPrice[i] = a.tuNew;
      out.repeatTechnicalUnitPrice[i] = a.tuRep;
      out.nominationRate[i] = a.nomRate;
      out.nominationCustomers[i] = a.nomCust;
      out.visitFrequency[i] = a.interval;
      out.newReturn[i] = a.nrr;
      return;
    }

    // Forecast row supplies the base; fall back to the anchor when a field is missing.
    const fcRow = readDriverRow(forecastByMonth[mk]) || {};
    const base = {};
    Object.keys(anchorRow).forEach((f) => { base[f] = fcRow[f] ?? anchorRow[f]; });
    if (!base.count || base.sales == null) return;
    if (base.newC == null || base.repC == null) {
      // Only the total is known: keep the anchor's mix.
      const mix = anchorRow.newC != null && anchorRow.count ? anchorRow.newC / anchorRow.count : 0.2;
      base.newC = base.count * mix;
      base.repC = base.count - base.newC;
    }

    const r0 = (base.nomRate ?? 0) / 100;
    const b = {
      newC: base.newC,
      repC: base.repC,
      r: r0,
      uFree: (base.sales / base.count) / (1 + r0 * K),
    };

    // ---- pins this month (focusKey → value on its 実績→目標 path) ----
    const monthPins = {};
    for (const fk of PIN_FOCUS_KEYS) {
      const v = P(fk, i);
      if (v != null) monthPins[fk] = v;
    }

    // Solve 売上 = 客数 × 単価 with a given set of pins held as equalities.
    const solveMonth = (pinSet) => {
      const pin = (fk) => (pinSet[fk] != null ? pinSet[fk] : null);
      const pinNew = pin('newCustomers');
      const pinRep = pin('repeatCustomers');
      const pinCount = pin('customerCount');
      const pinNomRate = pin('nomination');
      const pinNomCust = pin('nominationCustomers');
      const pinUnit = pin('unitPrice');
      const pinNrr = pin('newReturn');
      const pinInterval = pin('visitFreq');

      const fixed = {};
      if (pinNew != null) fixed.newC = pinNew;
      if (pinRep != null) fixed.repC = pinRep;
      if (pinRep == null) {
        // 再来率 / 来店周期 pins act on リピ — unless リピ itself is pinned.
        let rep = b.repC;
        let touched = false;
        if (pinNrr != null) {
          const mk3 = monthKeyOffset(mk, -3);
          const n3 =
            readDriverRow(historyByMonth[mk3])?.newC ??
            readDriverRow(forecastByMonth[mk3])?.newC ??
            base.newC;
          rep += ((pinNrr - (base.nrr ?? 0)) / 100) * (n3 ?? 0);
          touched = true;
        }
        if (pinInterval != null && base.interval && pinInterval > 0) {
          rep *= base.interval / pinInterval;
          touched = true;
        }
        if (touched) fixed.repC = Math.max(0, rep);
      }
      if (pinCount != null) {
        const s = pinCount / (b.newC + b.repC);
        fixed.newC = b.newC * s;
        fixed.repC = b.repC * s;
      }
      if (pinNomRate != null) fixed.r = Math.min(1, Math.max(0, pinNomRate / 100));

      const free = ['newC', 'repC', 'r', 'uFree'].filter((d) => {
        if (fixed[d] != null) return false;
        if (pinUnit != null && (d === 'r' || d === 'uFree')) return false; // 単価 pinned: no lever left there
        if (pinNomCust != null && (d === 'newC' || d === 'repC')) return false; // 客数 follows 指名客数 ÷ 指名率
        if (pinNomCust != null && d === 'r') return false; // with 客数 = 指名客数 ÷ 指名率, a free 指名率 would shrink 売上 as it rises
        if (d === 'r' && K <= 0) return false; // no 指名 premium in the data → 指名率 moves nothing
        return true;
      });

      const evalAt = (t) => {
        const m = (d) => (free.includes(d) ? Math.exp(t * (weights[d] ?? 1)) : 1);
        const r = fixed.r ?? Math.min(1, b.r * m('r'));
        const uFree = b.uFree * m('uFree');
        let newC = fixed.newC ?? b.newC * m('newC');
        let repC = fixed.repC ?? b.repC * m('repC');
        let N;
        if (pinNomCust != null && r > 0) {
          N = pinNomCust / r;
          const mix = b.newC + b.repC > 0 ? b.newC / (b.newC + b.repC) : 0.2;
          newC = N * mix;
          repC = N - newC;
        } else {
          N = newC + repC;
        }
        const U = pinUnit ?? uFree * (1 + r * K);
        return { N, U, r, newC, repC, sales: N * U };
      };

      let sol = evalAt(0);
      if (free.length > 0) {
        let lo = -8;
        let hi = 8;
        for (let it = 0; it < 80; it += 1) {
          const t = (lo + hi) / 2;
          sol = evalAt(t);
          if (sol.sales < R) lo = t; else hi = t;
        }
      }
      let { N, U, r, newC, repC } = sol;
      let closedByOneSide = false;
      if (Math.abs(sol.sales - R) > 1) {
        // Pins fix one side of 売上 = 客数 × 単価 entirely; the other side must close it.
        closedByOneSide = true;
        if (pinUnit != null) {
          N = R / U;
          const mix = newC + repC > 0 ? newC / (newC + repC) : 0.2;
          newC = N * mix;
          repC = N - newC;
        } else {
          U = R / N;
        }
      }

      // 店販 / 技術: pin, else a STABLE share of 売上 — the closed-month average, not
      // the month's forecast. The retail forecast is a day-rate YoY branch that swings
      // 0 → 3,800 → 17,240 on one year of sparse data, and 必要 must not inherit
      // that saw-tooth. Falls back to the forecast share only when history has none.
      let retail = pin('retailSales');
      const pinTech = pin('techSales');
      if (pinTech != null) retail = R - pinTech;
      if (retail == null) {
        const fcShare = base.sales && base.retail != null ? base.retail / base.sales : 0;
        retail = R * (histRetailShare ?? fcShare);
      }
      let overran = false;
      if (retail > R) { retail = R; overran = true; }
      if (retail < 0) retail = 0;

      return { N, U, r, newC, repC, retail, tech: R - retail, free, closedByOneSide, overran };
    };

    // A 目標 on a KPI means "at least this" (or "at most", for 来店周期), not
    // "exactly this". A pin the solution already clears without it is dropped, so
    // 指名客数 20 cannot cap 客数 at 40 when the goal needs 90 customers anyway.
    let active = { ...monthPins };
    let sol = solveMonth(active);
    const relaxedHere = [];
    for (let pass = 0; pass < 4; pass += 1) {
      let dropped = false;
      for (const fk of Object.keys(active)) {
        if (!RELAXABLE_PINS[fk]) continue;
        const without = { ...active };
        delete without[fk];
        const trial = solveMonth(without);
        const natural = RELAXABLE_PINS[fk](trial);
        if (natural != null && natural >= active[fk]) {
          delete active[fk];
          sol = trial;
          dropped = true;
          relaxedHere.push(fk);
          break;
        }
      }
      if (!dropped) break;
    }
    const { N, U, r, newC, repC, retail, tech } = sol;
    relaxedPins = relaxedHere;
    relaxedByMonth[mk] = relaxedHere;
    freeDrivers = sol.free;
    if (sol.closedByOneSide) pinsFixEverything = true;
    if (sol.overran) retailOverran = true;
    const pinNomCust = active.nominationCustomers ?? null;
    const pinInterval = active.visitFreq ?? null;
    const pinNrr = active.newReturn ?? null;

    // ---- 単価 ----
    const techUnit = N ? tech / N : null;
    let tuNew = base.tuNew;
    let tuRep = base.tuRep;
    if (tuNew != null && tuRep != null && newC * tuNew + repC * tuRep > 0) {
      const k = tech / (newC * tuNew + repC * tuRep);
      tuNew *= k;
      tuRep *= k;
    } else if (tuRep != null && repC > 0 && tuNew == null) {
      // History carries only one 技術単価: hold the mix, scale the level.
      tuNew = techUnit;
      tuRep = (tech - newC * tuNew) / repC;
    } else {
      tuNew = techUnit;
      tuRep = techUnit;
    }

    // ---- 指名 / soft KPIs ----
    const nomCust = pinNomCust ?? N * r;
    let interval = pinInterval;
    if (interval == null && base.interval != null && base.repC > 0 && repC > 0) {
      // More repeats than forecast ⇒ shorter interval. Never longer: a plan cannot
      // ask customers to come back LESS often than they already would (a month where
      // 売上必要 dips under 予測 — e.g. right after a partial anchor month — must not
      // turn into "wait 73 days").
      interval = base.interval * Math.min(1, base.repC / repC);
    }
    // 新規再来率: pinned when it is a goal; otherwise the inverse of the pin rule above —
    // the plan needs this many more returning customers than the forecast base, so the
    // return rate must move by the same ratio (0–100%). Owner request 2026-10-04: every
    // chart metric shows 必要 once 売上 has a target.
    let nrr = pinNrr ?? null;
    if (nrr == null && base.nrr != null && base.repC > 0 && repC != null) {
      const k = repC / base.repC;
      nrr = Number.isFinite(k) ? Math.min(100, Math.max(0, base.nrr * k)) : base.nrr;
    }

    out.retailSales[i] = retail;
    out.techSales[i] = tech;
    out.customerCount[i] = N;
    out.customerUnitPrice[i] = U;
    out.techUnitPrice[i] = techUnit;
    out.newCustomerCount[i] = newC;
    out.repeatCustomerCount[i] = repC;
    out.newTechnicalUnitPrice[i] = tuNew;
    out.repeatTechnicalUnitPrice[i] = tuRep;
    out.nominationRate[i] = r * 100;
    out.nominationCustomers[i] = nomCust;
    out.visitFrequency[i] = interval;
    out.newReturn[i] = nrr;

    // ---- flags, not caps ----
    const flag = (key, value, label) => {
      const bd = band[key];
      if (!bd || value == null) return;
      if (value > bd.max) feasibility.push({ month: mk, key: label, value, bound: 'max', limit: bd.max });
      else if (value < bd.min) feasibility.push({ month: mk, key: label, value, bound: 'min', limit: bd.min });
    };
    flag('unit', U, 'customerUnitPrice');
    flag('count', N, 'customerCount');
    flag('nomRate', r * 100, 'nominationRate');
    flag('newC', newC, 'newCustomerCount');
    flag('repC', repC, 'repeatCustomerCount');
  });

  const series = {};
  const published = [];
  keys.forEach((k) => {
    const hasValue = out[k].some((v, i) => i > lastActualIdx && v != null);
    series[k] = hasValue ? out[k] : null;
    if (hasValue) published.push(k);
  });

  return {
    series,
    meta: {
      source: 'driver_plan',
      published,
      freeDrivers,
      premium,
      weights,
      retailOverran,
      pinsFixEverything,
      relaxedPins,
      relaxedByMonth,
      feasibility,
    },
  };
}

/** 注力 keys the driver plan understands as pins. */
const PIN_FOCUS_KEYS = [
  'nomination', 'nominationCustomers', 'unitPrice', 'customerCount', 'retailSales',
  'techSales', 'newCustomers', 'repeatCustomers', 'newReturn', 'visitFreq',
];

/**
 * Pins that are floors ("at least"): value the solution gives that KPI when the
 * pin is not held. 来店周期 / 再来率 have no natural value here and are always held.
 */
const RELAXABLE_PINS = {
  nomination: (s) => s.r * 100,
  nominationCustomers: (s) => s.N * s.r,
  unitPrice: (s) => s.U,
  customerCount: (s) => s.N,
  newCustomers: (s) => s.newC,
  repeatCustomers: (s) => s.repC,
  retailSales: (s) => s.retail,
  techSales: (s) => s.tech,
};

/** One vocabulary for API history rows and ml_forecast rows. */
export function readDriverRow(row) {
  if (!row) return null;
  const sales = num(row.total_sales);
  const retail = num(row.retail_sales);
  const newC = num(row.new_customer_count ?? row.new_customers);
  const repC = num(row.repeat_customer_count ?? row.repeat_customers);
  const count =
    num(row.customer_count ?? row.total_customers) ??
    (newC != null && repC != null ? newC + repC : null);
  return {
    sales,
    retail,
    count: count && count > 0 ? count : null,
    newC,
    repC,
    nomCust: num(row.nominated_customers),
    nomRate: num(row.nomination_rate),
    nomSales: num(row.nominated_customer_total_sales),
    freeSales: num(row.free_customer_total_sales),
    tuNew: num(row.new_technical_unit_price),
    tuRep: num(row.repeat_technical_unit_price ?? row.technical_unit_price),
    nrr: num(row.new_return_rate ?? row.new_customer_return_rate),
    interval: num(row.visit_frequency ?? row.avg_visit_interval_repeat),
  };
}

/** 指名客単価 ÷ フリー客単価 over the last 3 months that carry both; 1 when unknown. */
function nominationPremium(rows) {
  const ks = rows
    .slice(-3)
    .filter((r) => r.nomCust && r.nomSales != null && r.freeSales != null && r.count - r.nomCust > 0)
    .map((r) => (r.nomSales / r.nomCust) / (r.freeSales / (r.count - r.nomCust)))
    .filter((k) => Number.isFinite(k) && k > 0);
  if (ks.length === 0) return 1;
  return ks.reduce((a, b) => a + b, 0) / ks.length;
}

/** Coefficient of variation per driver over history; 1 when too little data. */
function driverWeights(rows) {
  const cv = (vals) => {
    const v = vals.filter((x) => x != null && Number.isFinite(x));
    if (v.length < 3) return 1;
    const mean = v.reduce((a, b) => a + b, 0) / v.length;
    if (!mean) return 1;
    const varc = v.reduce((a, b) => a + (b - mean) ** 2, 0) / v.length;
    const c = Math.sqrt(varc) / Math.abs(mean);
    return c > 0 ? c : 1;
  };
  const w = {
    newC: cv(rows.map((r) => r.newC)),
    repC: cv(rows.map((r) => r.repC)),
    r: cv(rows.map((r) => r.nomRate)),
    uFree: cv(rows.map((r) => (r.count ? r.sales / r.count : null))),
  };
  // A flat history (all CV 0 → 1) must not privilege any driver.
  return w;
}

function personalBand(rows) {
  const b = {};
  const put = (k, vals) => {
    const v = vals.filter((x) => x != null && Number.isFinite(x));
    if (v.length) b[k] = { min: Math.min(...v), max: Math.max(...v) };
  };
  put('unit', rows.map((r) => (r.count ? r.sales / r.count : null)));
  put('count', rows.map((r) => r.count));
  put('nomRate', rows.map((r) => r.nomRate));
  put('newC', rows.map((r) => r.newC));
  put('repC', rows.map((r) => r.repC));
  return b;
}

/** Customer count from a history/forecast row (prefer total, else new+repeat). */
export function rowCustomerCount(row) {
  if (!row) return null;
  const direct = row.customer_count ?? row.total_customers;
  if (direct != null && !Number.isNaN(Number(direct)) && Number(direct) > 0) {
    return Number(direct);
  }
  const n = Number(row.new_customer_count ?? row.new_customers);
  const r = Number(row.repeat_customer_count ?? row.repeat_customers);
  if (Number.isFinite(n) && Number.isFinite(r) && n + r > 0) return n + r;
  return null;
}

const RATIO_CAP_LO = 0.25;
const RATIO_CAP_HI = 4;

function clampRatio(r) {
  if (r == null || Number.isNaN(r)) return null;
  return Math.min(RATIO_CAP_HI, Math.max(RATIO_CAP_LO, r));
}

function num(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

/** Focus keys that drive the scenario identities. */
export const SCENARIO_OVERRIDE_KEYS = [
  'unitPrice',
  'customerCount',
  'retailSales',
  'newReturn',
  'nomination',
  'totalSales',
];

/**
 * Build the「目標達成に必要」scenario: base = ML forecast, with 注力①/② replaced by
 * their required paths, then every dependent KPI recomputed from the identities so
 * the whole table reconciles.
 *
 *   客数        = 新規客数 + リピート客数   (新規再来率 scales リピート)
 *   売上        = 客数 × 客単価
 *   店販売上    = override, else base 店販比率 × 売上
 *   技術売上    = 売上 − 店販売上
 *   技術単価    = 技術売上 / 客数
 *
 * 売上 is therefore derived math from the focus targets — it is not re-anchored onto
 * the KGI, so a shortfall vs `salesTarget` stays visible (`meta.shortfall`).
 *
 * @param {object} args
 * @param {string[]} args.monthKeys
 * @param {number} args.lastActualIdx
 * @param {Record<string, object>} [args.historyByMonth]
 * @param {Record<string, object>} [args.forecastByMonth]
 * @param {Record<string, (number|null)[]>} [args.overrides] focusKey → required series
 * @returns {{ rows: (object|null)[]|null, meta: object }}
 */
export function buildScenarioRequired({
  monthKeys,
  lastActualIdx,
  historyByMonth = {},
  forecastByMonth = {},
  overrides = {},
}) {
  const appliedKeys = SCENARIO_OVERRIDE_KEYS.filter(
    (k) =>
      Array.isArray(overrides[k]) &&
      overrides[k].length === (monthKeys?.length ?? -1) &&
      overrides[k].some((v, i) => i > lastActualIdx && v != null)
  );

  if (
    !Array.isArray(monthKeys) ||
    monthKeys.length === 0 ||
    lastActualIdx == null ||
    lastActualIdx < 0 ||
    lastActualIdx >= monthKeys.length ||
    appliedKeys.length === 0
  ) {
    return { rows: null, meta: { source: 'none', appliedKeys: [] } };
  }

  const ov = (key, i) => (appliedKeys.includes(key) ? num(overrides[key][i]) : null);

  const lastHist = historyByMonth[monthKeys[lastActualIdx]] || {};
  const lastTotal = num(lastHist.total_sales);
  const lastRetail = num(lastHist.retail_sales);
  const lastNew = num(lastHist.new_customer_count);
  const lastRepeat = num(lastHist.repeat_customer_count);
  const lastCount = rowCustomerCount(lastHist);
  const lastUnit = lastTotal != null && lastCount ? lastTotal / lastCount : null;
  const lastNrr = num(lastHist.new_return_rate ?? lastHist.new_customer_return_rate);
  const lastNomination = num(lastHist.nomination_rate);
  // Mix ratios held from the last 実績: new-customer unit price vs overall, 指名 share of 売上.
  const lastNewSales = num(lastHist.total_sales_new);
  const lastNomSales = num(lastHist.nominated_customer_total_sales);
  const lastNomCust = num(lastHist.nominated_customers);
  const newUnitRatio =
    lastNewSales != null && lastNew && lastUnit ? lastNewSales / lastNew / lastUnit : 1;
  const nomShareOfSales = lastNomSales != null && lastTotal ? lastNomSales / lastTotal : null;
  const nomRateIsPct = lastNomination != null && lastNomination > 1;

  const composeRow = (total, retail, count, newC, repeatC, unit, nomination, nrr) => {
    const tech = total != null && retail != null ? total - retail : null;
    // 指名客数 = 客数 × 指名率 (rate may be stored as % or ratio); else hold the last count.
    let nomCust = null;
    if (count != null && nomination != null) {
      nomCust = count * (nomRateIsPct ? nomination / 100 : nomination);
    } else if (lastNomCust != null) {
      nomCust = lastNomCust;
    }
    // 指名売上: last 実績 share of 売上, scaled by how the 指名 mix moves.
    let nomSales = null;
    if (total != null && nomShareOfSales != null) {
      const r =
        lastNomCust && nomCust != null && lastCount && count
          ? nomCust / count / (lastNomCust / lastCount)
          : 1;
      nomSales = total * nomShareOfSales * (Number.isFinite(r) ? r : 1);
    }
    const newSales = newC != null && unit != null ? newC * unit * newUnitRatio : null;
    const repeatSales = total != null && newSales != null ? total - newSales : null;
    return {
      total_sales: total,
      retail_sales: retail,
      tech_sales: tech,
      customer_count: count,
      new_customer_count: newC,
      repeat_customer_count: repeatC,
      unit_price: unit,
      tech_unit_price: tech != null && count ? tech / count : null,
      nomination_rate: nomination,
      new_return_rate: nrr,
      nominated_customers: nomCust,
      nomination_sales: nomSales,
      new_sales: newSales,
      repeat_sales: repeatSales,
    };
  };

  const rows = monthKeys.map((mk, i) => {
    if (i < lastActualIdx) return null;
    if (i === lastActualIdx) {
      return composeRow(
        lastTotal,
        lastRetail,
        lastCount,
        lastNew,
        lastRepeat,
        lastUnit,
        lastNomination,
        lastNrr
      );
    }

    const fc = forecastByMonth[mk] || {};
    const baseTotal = num(fc.total_sales) ?? lastTotal;
    const baseRetail = num(fc.retail_sales) ?? lastRetail;
    const baseNew = num(fc.new_customer_count) ?? lastNew;
    const baseRepeat = num(fc.repeat_customer_count) ?? lastRepeat;
    const baseCount =
      rowCustomerCount(fc) ??
      (baseNew != null && baseRepeat != null ? baseNew + baseRepeat : lastCount);
    const baseUnit = baseTotal != null && baseCount ? baseTotal / baseCount : lastUnit;
    const baseNomination = num(fc.nomination_rate) ?? lastNomination;
    const retailShare =
      baseTotal ? (baseRetail != null ? baseRetail / baseTotal : null) : null;

    let newC = baseNew;
    let repeatC = baseRepeat;
    let count = baseCount;
    let unit = baseUnit;
    let nomination = baseNomination;
    let nrr = lastNrr;

    // 新規再来率 → more returning customers (within-month proxy; full stock-flow deferred)
    const nrrReq = ov('newReturn', i);
    if (nrrReq != null) {
      nrr = nrrReq;
      const r = lastNrr ? clampRatio(nrrReq / lastNrr) : null;
      if (r != null && repeatC != null) repeatC *= r;
    }
    if (newC != null && repeatC != null) count = newC + repeatC;

    // 総客数 target wins on the total; keep the new/repeat mix
    const countReq = ov('customerCount', i);
    if (countReq != null) {
      const r = count ? countReq / count : null;
      if (r != null && newC != null && repeatC != null) {
        newC *= r;
        repeatC *= r;
      }
      count = countReq;
    }

    const unitReq = ov('unitPrice', i);
    if (unitReq != null) unit = unitReq;

    const nominationReq = ov('nomination', i);
    if (nominationReq != null) nomination = nominationReq;

    let total = count != null && unit != null ? count * unit : baseTotal;

    // 売上 itself as 注力 (KGI): honour it and back out 客数 from the unit price
    const totalReq = ov('totalSales', i);
    if (totalReq != null) {
      total = totalReq;
      if (countReq == null && unit) {
        count = total / unit;
        const r = baseCount ? count / baseCount : null;
        if (r != null && newC != null && repeatC != null) {
          newC = baseNew * r;
          repeatC = baseRepeat * r;
        }
      } else if (count) {
        unit = total / count;
      }
    }

    // 新規再来率 without its own target: the inverse of the rule above. Reaching the 売上 /
    // 客数 target needs this many more (or fewer) returning customers than the forecast
    // base, so the return rate has to move by the same ratio (clamped to 0–100%).
    if (nrrReq == null) {
      const baseNrr = num(fc.new_return_rate ?? fc.new_customer_return_rate) ?? lastNrr;
      if (baseNrr != null && baseRepeat && repeatC != null) {
        const r = repeatC / baseRepeat;
        nrr = Number.isFinite(r) ? Math.min(100, Math.max(0, baseNrr * r)) : baseNrr;
      } else if (baseNrr != null) {
        nrr = baseNrr;
      }
    }

    let retail = ov('retailSales', i);
    if (retail == null) {
      retail = retailShare != null && total != null ? total * retailShare : baseRetail;
    } else if (unitReq == null && countReq == null && nrrReq == null && totalReq == null) {
      // 店販 is the only lever: technical side stays on forecast, retail lifts the total
      const baseTech = baseTotal != null && baseRetail != null ? baseTotal - baseRetail : null;
      if (baseTech != null) {
        total = baseTech + retail;
        if (count) unit = total / count;
      }
    }

    return composeRow(total, retail, count, newC, repeatC, unit, nomination, nrr);
  });

  return {
    rows,
    meta: {
      source: 'scenario',
      appliedKeys,
      lastActualIdx,
    },
  };
}

/** Pull one scenario field into a chart-aligned series. */
export function scenarioSeries(rows, field) {
  if (!Array.isArray(rows) || !field) return null;
  const series = rows.map((r) => {
    const v = r ? num(r[field]) : null;
    return v != null && Number.isFinite(v) ? v : null;
  });
  return series.some((v) => v != null) ? series : null;
}

/**
 * Re-anchor a shaped path C[] (same length as monthKeys) onto `target` at deadline
 * via Option A, preserving C's curvature relative to lastActual.
 */
function reanchorToTarget({
  monthKeys,
  lastActualIdx,
  lastActualValue,
  target,
  goalMonthKey,
  shaped,
  planTargets = {},
  source,
}) {
  return buildRequiredSeries({
    monthKeys,
    lastActualIdx,
    lastActualValue,
    target,
    goalMonthKey,
    planTargets,
    source,
    forecast: shaped,
  });
}

/**
 * Map a focus-KPI required path → 売上 required path.
 *
 * Revenue formula (v2):
 * 1) Focus required = forecast-shaped blend → focusTarget (buildRequiredSeries).
 * 2) Identity compound when focusKey ∈ {retailSales, unitPrice, customerCount}:
 *    retail: (fc.sales − fc.retail) + retail_req
 *    unitPrice: unit_req × customers
 *    customerCount: count_req × spend
 *    Then re-anchor compound C[] → salesTarget (Option A) so mokuhyo is hit.
 * 3) Else if focus_req + focus_fc + sales_fc: modulate sales_fc by
 *    clamp(focus_req/focus_fc, 0.25..4) (avg with subFocus when both);
 *    re-anchor → salesTarget. Full causal compound deferred for NRR/%.
 * 4) Else: forecast-shaped sales blend → salesTarget (linear if no sales fc).
 *
 * @returns {{ series: (number|null)[]|null, meta: object }}
 */
export function buildDriverRevenueRequired({
  monthKeys,
  lastActualIdx,
  lastSalesActual,
  salesTarget,
  goalMonthKey,
  focusKey,
  focusLastActual,
  focusTarget,
  forecastByMonth = {},
  historyByMonth = {},
  planTargetsSales = {},
  planTargetsFocus = {},
  salesForecast = null,
  focusForecast = null,
  subFocusKey = null,
  subFocusLastActual = null,
  subFocusTarget = null,
  subFocusForecast = null,
  planTargetsSubFocus = {},
}) {
  const shapedSales = () =>
    buildRequiredSeries({
      monthKeys,
      lastActualIdx,
      lastActualValue: lastSalesActual,
      target: salesTarget,
      goalMonthKey,
      planTargets: planTargetsSales,
      source: 'deadline',
      forecast: salesForecast,
    });

  if (!focusKey || focusKey === 'totalSales') {
    return shapedSales();
  }

  const focusShaped = buildRequiredSeries({
    monthKeys,
    lastActualIdx,
    lastActualValue: focusLastActual,
    target: focusTarget,
    goalMonthKey,
    planTargets: planTargetsFocus,
    source: 'focus_shaped',
    forecast: focusForecast,
  });

  if (!focusShaped.series) {
    return shapedSales();
  }

  const lastMk = monthKeys[lastActualIdx];
  const lastHist = historyByMonth[lastMk] || {};
  const lastCount = rowCustomerCount(lastHist);
  const lastSpend =
    lastSalesActual != null && lastCount
      ? Number(lastSalesActual) / lastCount
      : null;

  if (focusKey === 'retailSales') {
    const compound = monthKeys.map((mk, i) => {
      if (i < lastActualIdx) return null;
      const retailReq = focusShaped.series[i];
      if (retailReq == null || Number.isNaN(Number(retailReq))) return null;
      if (i === lastActualIdx) return Number(lastSalesActual);

      const fc = forecastByMonth[mk] || {};
      const ts = fc.total_sales != null ? Number(fc.total_sales) : null;
      const rs = fc.retail_sales != null ? Number(fc.retail_sales) : null;
      if (ts != null && rs != null && !Number.isNaN(ts) && !Number.isNaN(rs)) {
        return ts - rs + Number(retailReq);
      }
      const lastRs = lastHist.retail_sales != null ? Number(lastHist.retail_sales) : 0;
      const nonRetail = Number(lastSalesActual) - lastRs;
      return nonRetail + Number(retailReq);
    });
    const anchored = reanchorToTarget({
      monthKeys,
      lastActualIdx,
      lastActualValue: lastSalesActual,
      target: salesTarget,
      goalMonthKey,
      shaped: compound,
      planTargets: planTargetsSales,
      source: 'driver_retail',
    });
    return {
      series: anchored.series,
      meta: {
        ...anchored.meta,
        source: 'driver_retail',
        focusKey,
        focusShape: focusShaped.meta.shape,
        infeasible: false,
      },
    };
  }

  if (focusKey === 'unitPrice') {
    const compound = monthKeys.map((mk, i) => {
      if (i < lastActualIdx) return null;
      const unitReq = focusShaped.series[i];
      if (unitReq == null || Number.isNaN(Number(unitReq))) return null;
      if (i === lastActualIdx) return Number(lastSalesActual);

      const fc = forecastByMonth[mk] || historyByMonth[mk] || {};
      const count = rowCustomerCount(fc) ?? lastCount;
      if (!count) return null;
      return Number(unitReq) * count;
    });
    const anchored = reanchorToTarget({
      monthKeys,
      lastActualIdx,
      lastActualValue: lastSalesActual,
      target: salesTarget,
      goalMonthKey,
      shaped: compound,
      planTargets: planTargetsSales,
      source: 'driver_unitPrice',
    });
    return {
      series: anchored.series,
      meta: {
        ...anchored.meta,
        source: 'driver_unitPrice',
        focusKey,
        focusShape: focusShaped.meta.shape,
        decompose: 'unit_x_customers',
      },
    };
  }

  if (focusKey === 'customerCount') {
    const compound = monthKeys.map((mk, i) => {
      if (i < lastActualIdx) return null;
      const countReq = focusShaped.series[i];
      if (countReq == null || Number.isNaN(Number(countReq))) return null;
      if (i === lastActualIdx) return Number(lastSalesActual);

      const fc = forecastByMonth[mk] || {};
      let spend = lastSpend;
      const fcCount = rowCustomerCount(fc);
      if (fc.total_sales != null && fcCount) {
        spend = Number(fc.total_sales) / fcCount;
      }
      if (!spend) return null;
      return Number(countReq) * spend;
    });
    const anchored = reanchorToTarget({
      monthKeys,
      lastActualIdx,
      lastActualValue: lastSalesActual,
      target: salesTarget,
      goalMonthKey,
      shaped: compound,
      planTargets: planTargetsSales,
      source: 'driver_customerCount',
    });
    return {
      series: anchored.series,
      meta: {
        ...anchored.meta,
        source: 'driver_customerCount',
        focusKey,
        focusShape: focusShaped.meta.shape,
        decompose: 'count_x_spend',
      },
    };
  }

  // newReturn / nomination / unknown: ratio-modulate sales forecast when possible.
  const hasSalesFc =
    Array.isArray(salesForecast) &&
    salesForecast.length === monthKeys.length &&
    salesForecast.some((v, i) => i > lastActualIdx && v != null);
  const hasFocusFc =
    Array.isArray(focusForecast) &&
    focusForecast.length === monthKeys.length &&
    focusForecast.some((v, i) => i > lastActualIdx && v != null);

  let subShaped = null;
  if (subFocusKey && subFocusLastActual != null && subFocusTarget != null) {
    subShaped = buildRequiredSeries({
      monthKeys,
      lastActualIdx,
      lastActualValue: subFocusLastActual,
      target: subFocusTarget,
      goalMonthKey,
      planTargets: planTargetsSubFocus,
      source: 'subfocus_shaped',
      forecast: subFocusForecast,
    });
  }
  const hasSubFc =
    subShaped?.series &&
    Array.isArray(subFocusForecast) &&
    subFocusForecast.length === monthKeys.length &&
    subFocusForecast.some((v, i) => i > lastActualIdx && v != null);

  if (hasSalesFc && hasFocusFc && focusShaped.series) {
    const modulated = monthKeys.map((_, i) => {
      if (i < lastActualIdx) return null;
      if (i === lastActualIdx) return Number(lastSalesActual);
      const sf = salesForecast[i];
      if (sf == null || Number.isNaN(Number(sf))) return null;

      const ratios = [];
      const ff = focusForecast[i];
      const fr = focusShaped.series[i];
      if (ff != null && Number(ff) !== 0 && fr != null) {
        const r = clampRatio(Number(fr) / Number(ff));
        if (r != null) ratios.push(r);
      }
      if (hasSubFc) {
        const sff = subFocusForecast[i];
        const sfr = subShaped.series[i];
        if (sff != null && Number(sff) !== 0 && sfr != null) {
          const r = clampRatio(Number(sfr) / Number(sff));
          if (r != null) ratios.push(r);
        }
      }
      const ratio =
        ratios.length > 0
          ? ratios.reduce((a, b) => a + b, 0) / ratios.length
          : 1;
      return Number(sf) * ratio;
    });

    const anchored = reanchorToTarget({
      monthKeys,
      lastActualIdx,
      lastActualValue: lastSalesActual,
      target: salesTarget,
      goalMonthKey,
      shaped: modulated,
      planTargets: planTargetsSales,
      source: 'deadline_focus_ratio',
    });
    return {
      series: anchored.series,
      meta: {
        ...anchored.meta,
        source: 'deadline_focus_ratio',
        focusKey,
        subFocusKey: subFocusKey || undefined,
        note: 'Sales fc × clamp(focus_req/focus_fc); full causal compound deferred',
      },
    };
  }

  const sales = shapedSales();
  return {
    series: sales.series,
    meta: {
      ...sales.meta,
      source: sales.meta.source === 'legacy' ? 'legacy' : 'deadline_focus_unmapped',
      focusKey,
      note:
        focusKey === 'newReturn'
          ? 'NRR needs stock-flow (PIPELINE_LAG); sales path uses sales-forecast blend'
          : 'No identity to sales; sales path uses sales-forecast blend',
    },
  };
}

/** Fields that move「目標達成に必要」when POS/forecast rows change. */
const FINGERPRINT_ROW_FIELDS = [
  'total_sales',
  'retail_sales',
  'new_customer_count',
  'repeat_customer_count',
  'customer_count',
  'nomination_rate',
  'nominated_customers',
  'new_return_rate',
  'new_customer_return_rate',
  'visit_frequency',
  'new_technical_unit_price',
  'repeat_technical_unit_price',
];

function fingerprintRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return '';
  const sorted = [...rows].sort((a, b) => String(a?.month || '').localeCompare(String(b?.month || '')));
  return sorted
    .map((r) => {
      if (!r?.month) return '';
      const parts = [r.month];
      for (const f of FINGERPRINT_ROW_FIELDS) {
        const v = r[f];
        parts.push(v == null || Number.isNaN(Number(v)) ? '' : String(Number(v)));
      }
      return parts.join(':');
    })
    .join('|');
}

function fingerprintGoals(goals) {
  if (!goals || typeof goals !== 'object') return '';
  const plan = Array.isArray(goals.planMonths)
    ? goals.planMonths
        .map((p) =>
          [
            p?.monthKey || '',
            p?.salesTarget ?? '',
            p?.focusTarget ?? '',
            p?.unitTarget ?? '',
          ].join(':')
        )
        .sort()
        .join('|')
    : '';
  const chart =
    goals.chartTargets && typeof goals.chartTargets === 'object'
      ? Object.keys(goals.chartTargets)
          .sort()
          .map((k) => `${k}:${goals.chartTargets[k]}`)
          .join('|')
      : '';
  // Every goal field that moves a chart: a field missing here leaves the cached chart stale
  return [
    goals.focusKey ?? '',
    goals.subFocusKey ?? '',
    goals.focusTarget ?? '',
    goals.subFocusTarget ?? '',
    goals.thirdFocusKey ?? '',
    goals.thirdFocusTarget ?? '',
    goals.subKgiKey ?? '',
    goals.subKgiTarget ?? '',
    goals.salesTarget ?? '',
    goals.finalSales ?? '',
    goals.goalDate ?? '',
    goals.planLength ?? '',
    plan,
    chart,
  ].join(';');
}

/**
 * Stable fingerprint of everything that should trigger a「必要」recompute:
 * goals + POS history + ML forecast. Unchanged → skip rebuild.
 */
export function buildRequiredInputFingerprint(kpiHistory, mlForecasts, goals) {
  return [
    'g=' + fingerprintGoals(goals),
    'p=' + fingerprintRows(kpiHistory),
    'f=' + fingerprintRows(mlForecasts),
  ].join('\n');
}
